import { Box, Typography } from '@mui/material'
import moment from 'moment'
import type React from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'

import type { Application, Address, Document } from '@/types/Activity'
import type { OutcomeStatus } from '@/types/Outcome'
import { Colours } from '@/utils/styles'

import EditAddress from './EditAddress'
import LandlordReference from './EntityPanels/ResidentialCard/LandlordReference'
import { ButtonsContainer } from './IdentityCard/IdentityCard'
import Pill from './Pill'
import UnableToVerifyAddress from './UnableToVerifyAddress'
import VerifyAddress from './VerifyAddress'

interface Props {
  addressObj: Address
  index: number
  application: Application
  onOpenResEvidence: (index: number) => void
  getProofMetaData: (array1: Document[], filename: string) => string | null
  id?: string
  isCurrentAddress?: boolean
  subjectId: string
  customerId: string
  residentialOutcomeStatus?: OutcomeStatus
  isRestrictedReference?: boolean
}

const addressEntrySx = {
  py: '20px',
}

const addressHistoryItemSx = {
  display: 'grid',
  width: '100%',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  columnGap: '28px',
  rowGap: '12px',
  alignItems: 'flex-start',
  '@media (max-width:900px)': {
    gridTemplateColumns: '1fr',
    rowGap: '18px',
  },
}

const addressColumnSx = {
  fontSize: '14px',
  lineHeight: 1.35,
}

const addressTitleSx = {
  m: 0,
  fontSize: '14px',
  fontWeight: 700,
}

const addressValueSx = {
  display: 'block',
  mb: '12px',
  fontSize: '14px',
  lineHeight: 1.35,
}

const Capitalized = styled.span`
  text-transform: capitalize;
`

const AddressStatusHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-items: center;
  gap: 20px;

  h2 {
    margin: 2px 0px;
    text-transform: underline;
  }

  #address-status {
    color: ${Colours.teal};
  }
`

const getVerifyPillText = (status: string): string => {
  switch (status) {
    case 'unableToVerify':
      return 'Unable to verify'
    case 'verifiedByDocumentsAndAst':
      return 'Verified by documents and Tenancy Agreement'
  }

  return ''
}

const AddressEntryItem: React.FC<Props> = ({
  addressObj,
  application,
  isCurrentAddress,
  subjectId,
  customerId,
  residentialOutcomeStatus,
  isRestrictedReference,
}) => {
  const history = useHistory()
  const isRentingAddress = addressObj.status === 'Renting'
  const showAddressDetails = addressObj.addressType !== 'forwarding'
  const showPaymentDetails = isRentingAddress && !!addressObj.paymentDetails

  const navigateToLandlordReferee = (landlordId: string) =>
    history.push(`/landlord-referee/${landlordId}`)

  function addressTypeToString(addressType: string = '') {
    switch (addressType) {
      case 'current':
        return 'Current address'
      case 'previous':
        return 'Previous address'
      case 'financiallyAssociated':
        return 'Financially associated address'
      case 'forwarding':
        return 'Forwarding address'
      default:
        return ''
    }
  }
  return (
    <>
      <AddressStatusHeader>
        <h2>
          {addressTypeToString(addressObj.addressType)}{' '}
          {addressObj.status && (
            <>
              | <span id="address-status">{addressObj.status}</span>
            </>
          )}
        </h2>
        <ButtonsContainer>
          {isCurrentAddress && (
            <>
              <>
                <VerifyAddress
                  customerId={customerId}
                  subjectId={subjectId}
                  address={addressObj}
                  disabled={isRestrictedReference}
                />
                <UnableToVerifyAddress
                  customerId={customerId}
                  subjectId={subjectId}
                  address={addressObj}
                  disabled={isRestrictedReference}
                />
              </>
              {!isRestrictedReference && addressObj.id && addressObj.status && (
                <EditAddress
                  subjectId={subjectId}
                  customerId={customerId}
                  address={addressObj}
                />
              )}
            </>
          )}
        </ButtonsContainer>
        {(addressObj.verification?.state === 'unableToVerify' ||
          addressObj.verification?.state === 'verifiedByDocumentsAndAst') && (
          <div>
            <Pill type="info">{getVerifyPillText(addressObj.verification.state)}</Pill>
          </div>
        )}
      </AddressStatusHeader>
      <Box component="section" sx={addressEntrySx}>
        <Box sx={addressHistoryItemSx}>
          <Box sx={addressColumnSx}>
            <Capitalized>
              {addressObj.normalizedLine1 ? (
                <>
                  {addressObj.normalizedLine1.toLowerCase()}, <br />
                </>
              ) : null}
            </Capitalized>
            {addressObj.line2 ? (
              <>
                {addressObj.line2}, <br />
              </>
            ) : null}
            {addressObj.city ? (
              <>
                {addressObj.city}, <br />
              </>
            ) : null}
            {addressObj.county ? (
              <>
                {addressObj.county}, <br />
              </>
            ) : null}
            {addressObj.country ? (
              <>
                {addressObj.country}, <br />
              </>
            ) : null}
            {addressObj.postcode ? (
              <>
                {addressObj.postcode} <br />
              </>
            ) : null}
          </Box>
          {showAddressDetails && (
            <Box sx={addressColumnSx}>
              {addressObj.startDate && (
                <>
                  <Typography component="h2" sx={addressTitleSx}>
                    Move In Date
                  </Typography>
                  <Typography component="span" sx={addressValueSx}>
                    {moment(addressObj.startDate).format('Do MMMM YYYY')}
                  </Typography>
                </>
              )}
              <Typography component="h2" sx={addressTitleSx}>
                Years present
              </Typography>
              <Typography component="span" sx={addressValueSx}>
                {addressObj.durationAtAddress &&
                addressObj.durationAtAddress !== 'NaN Years'
                  ? addressObj.durationAtAddress
                  : 'Unable to calculate duration'}
              </Typography>
              <Typography component="h2" sx={addressTitleSx}>
                Electoral roll
              </Typography>
              <Typography component="span" sx={addressValueSx}>
                {addressObj.electoralRollConfirmation !== null &&
                addressObj.electoralRollConfirmation !== undefined &&
                addressObj.electoralRollConfirmation.status
                  ? 'Present'
                  : 'Not present'}
              </Typography>
              <Typography component="h2" sx={addressTitleSx}>
                Confirmed Years
              </Typography>
              <Typography component="span" sx={addressValueSx}>{`${
                addressObj.electoralRollConfirmation !== null &&
                addressObj.electoralRollConfirmation !== undefined &&
                addressObj.electoralRollConfirmation.yearsConfirmed
                  ? addressObj.electoralRollConfirmation.yearsConfirmed
                  : 'Not confirmed'
              }`}</Typography>
              {addressObj.addressType === 'current' && (
                <>
                  <Typography component="h2" sx={addressTitleSx}>
                    Status
                  </Typography>
                  <Typography component="span" sx={addressValueSx}>{`${
                    addressObj.status !== null && addressObj.status
                      ? addressObj.status
                      : 'Not confirmed'
                  }`}</Typography>
                </>
              )}
            </Box>
          )}
          {showPaymentDetails && (
            <Box sx={addressColumnSx}>
              <Typography component="h2" sx={addressTitleSx}>
                Rental payments
              </Typography>
              <Typography component="span" sx={addressValueSx}>{`${
                addressObj.paymentDetails?.isFromBankAccount ? 'Yes' : 'No'
              }`}</Typography>
              <Typography component="h2" sx={addressTitleSx}>
                Paid to
              </Typography>
              <Typography component="span" sx={addressValueSx}>
                {`${addressObj.paymentDetails?.recipient ?? 'Unknown'}`}
              </Typography>
              {addressObj.paymentDetails?.amount !== undefined &&
                addressObj.paymentDetails?.amount !== null && (
                  <>
                    <Typography component="h2" sx={addressTitleSx}>
                      Monthly payment
                    </Typography>
                    <Typography component="span" sx={addressValueSx}>
                      {'£' + addressObj.paymentDetails.amount.toFixed(2)}
                    </Typography>
                  </>
                )}
            </Box>
          )}
        </Box>
      </Box>
      {(addressObj.addressType === 'current' && addressObj.status) === 'Renting' && (
        <LandlordReference
          customerId={customerId}
          subjectId={subjectId}
          application={application}
          address={addressObj}
          reference={addressObj.reference}
          navigateToLandlordReferee={navigateToLandlordReferee}
          residentialOutcomeStatus={residentialOutcomeStatus}
          isRestrictedReference={isRestrictedReference}
        />
      )}
      <hr />
    </>
  )
}

export default AddressEntryItem
